import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box } from '@mui/material';
import { withStyles } from '@mui/styles';

import moment from 'moment';
import Interweave from 'interweave';

// dev-express table system
import { useQueryParams } from '../../components/providers/QueryParamsProvider';
import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';
import { useSavedSettings } from '../../components/tables/dev-express/hooks/useSavedSettings';
import { useBackfill } from '../../components/tables/dev-express/hooks/useBackfill';
import { useTableState } from '../../components/tables/dev-express/hooks/useTableState';
import { useQueryHandlers } from '../../components/tables/dev-express/hooks/useQueryHandlers';
import { useUiSettingsAutosave } from '../../components/tables/dev-express/hooks/useUiSettingsAutosave';
import { PrimaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/PrimaryToolbar';
import { SecondaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/SecondaryToolbar';
import { TableSearch } from '../../components/tables/dev-express/table-components/toolbar/TableSearch';
import { ColumnChooser } from '../../components/tables/dev-express/table-components/toolbar/ColumnChooser';
import { CsvDownloadButton } from '../../components/tables/dev-express/table-components/toolbar/CsvDownloadButton';
import { RefreshButton } from '../../components/tables/dev-express/table-components/toolbar/RefreshButton';
import { TableStateProvider } from '../../components/tables/dev-express/TableStateContext';
import { BaseTable } from '../../components/tables/dev-express/BaseTable';
import { RemotePagingPlugin } from '../../components/tables/dev-express/plugins/RemotePagingPlugin';
import { ColumnResizingPlugin } from '../../components/tables/dev-express/plugins/ColumnResizingPlugin';
import type { ColumnConfig } from '../../components/tables/dev-express/types/table.types';

import { getActionList, getConnectorScore } from './ConnectorService';
import SimpleTooltip from '../../components/SimpleTooltip';
import { MultiSelect } from '../../components/Selects';
import { PubSub } from '../../utils/eventUtils';
import MSScoreHistory from './MSScoreHistory';
import GCPScoreHistory from './GCPScoreHistory';
import SecurityStudioScoreHistory from './SecurityStudioScoreHistory';
import { toUniversalUtcDate } from '../../utils/date.utils';

const TABLE_ID = 'connectorDetails';
const BACKFILL = { size: 25, order: 'asc', orderBy: 'state' };

const styles = ({ palette }: any) => ({
  errorText: {
    color: `${palette.error.main} !important`,
  },
  errorIndicator: {
    marginRight: 2,
    position: 'relative',
    top: 2,
    left: 0,
  },
  iconStyle: {
    paddingLeft: 5,
    paddingRight: 5,
    width: '2.5rem !important',
    height: '20px !important',
    color: palette.components.globalHeader.contrastText,
  },
  headingMargins: {
    marginTop: '-4px',
  },
  flexBox: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
  },
  scoresSectionContainer: {
    borderRadius: 5,
    backgroundColor: palette.background.lighter,
    padding: 20,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1rem',
    lineHeight: 1.33,
    fontWeight: 'bold',
    paddingBottom: 2,
    margin: '0px !important',
    color: palette.text.text16,
  },
  totalScoresCol: {
    fontSize: '2.33rem',
    lineHeight: 1.43,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  totalScoresContainer: {
    padding: '0 2.5rem',
  },
  totalScoreTitle: {
    fontSize: '1.75rem',
    lineHeight: 1.18,
    color: palette.text.text14,
    margin: '0 0 1.5rem 0 !important',
  },
  myScore: {
    color: palette.primary.main,
    margin: '0 !important',
    fontSize: '1.75rem',
    lineHeight: 0.81,
    fontWeight: 600,
  },
  maxScore: {
    color: palette.text.primary,
  },
  otherScoresContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    flex: 'auto',
  },
  scoreLine: {
    display: 'flex',
    flexDirection: 'column',
  },
  scoresContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  verticalLine: {
    border: `1px solid ${palette.primary.oldBorder2}`,
    marginRight: 30,
  },
  noDataShow: {
    fontSize: '1.167rem',
    textAlign: 'right',
    lineHeight: 1.21,
    color: palette.text.primary,
    margin: '0 !important',
  },
  scoreHeaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    marginBottom: '1.5rem',
  },
  scoreHeader: {
    fontSize: '1.167rem',
    fontWeight: 600,
    color: palette.text.text14,
    margin: '0 !important',
  },
  sectionTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 20,
    color: palette.text.message,
    justifyContent: 'space-between',
    borderBottom: `1px solid ${palette.primary.modalBorder}`,
  },
  asOfContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  simpleTooltip: {
    '& $tooltip': {
      padding: 10,
      marginBottom: 5,
      border: `1px solid  ${palette.common.cowbellBlue}`,
      backgroundColor: palette.background.default,
      color: palette.text.text7,
      fontSize: '0.833rem',
    },
  },
  asOf: {
    fontSize: '1rem',
    lineHeight: 1.42,
    marginRight: 5,
    paddingTop: 2,
  },
  scoreHistoryContainer: {
    width: '100%',
    height: '200px',
    marginBottom: 20,
    backgroundColor: palette.background.lighter,
    padding: '13px 15px',
    borderRadius: 5,
  },
  scoresHistoryTitle: {
    margin: '0 0 10px 0 !important',
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: palette.text.text16,
    borderBottom: `1px solid ${palette.primary.modalBorder}`,
  },
  scoresHistoryContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
  },
  chartLegends: {
    width: '12.5rem',
    height: '20px',
    marginTop: 4,
  },
  chartsContainer: {
    width: '100%',
    height: '100%',
  },
  legendItem: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 3,
    cursor: 'pointer',
  },
  legendIcon4: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: palette.primary.mainYellow,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon4: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${palette.primary.mainYellow}`,
    margin: '3px 10px 0 0',
  },
  legendText4: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: palette.primary.mainYellow,
    margin: '0px !important',
  },
  legendIcon5: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: palette.text.text9,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon5: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${palette.text.text9}`,
    margin: '3px 10px 0 0',
  },
  legendText5: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: palette.text.text9,
    margin: '0px !important',
  },
  legendIcon6: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: palette.connectors.legend1,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon6: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${palette.connectors.legend1}`,
    margin: '3px 10px 0 0',
  },
  legendText6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: palette.connectors.legend1,
    margin: '0px !important',
  },

  legendIcon7: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: palette.connectors.legend2,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon7: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${palette.connectors.legend2}`,
    margin: '3px 10px 0 0',
  },
  legendText7: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: palette.connectors.legend2,
    margin: '0px !important',
  },

  legendIcon8: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: palette.connectors.legend3,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon8: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${palette.connectors.legend3}`,
    margin: '3px 10px 0 0',
  },
  legendText8: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: palette.connectors.legend3,
    margin: '0px !important',
  },

  legendIcon9: {
    width: '0.833rem',
    height: '0.833rem',
    backgroundColor: palette.connectors.legend4,
    margin: '3px 10px 0 0',
  },
  emptyLegendIcon9: {
    width: '0.833rem',
    height: '0.833rem',
    border: `1px solid ${palette.connectors.legend4}`,
    margin: '3px 10px 0 0',
  },
  legendText9: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    color: palette.connectors.legend4,
    margin: '0px !important',
  },
  dropdown: {
    margin: '0 0 10px 0',
  },
  button: {
    padding: '5px !important',
    border: `1px solid ${palette.primary.oldBorder2}`,
  },
  mediumCount: {
    color: palette.primary.modalBorder,
  },
});

const truncate = (value: string, len = 50) =>
  value.length > len ? `${value.substr(0, len)}...` : value;

const IsCompletedCell = ({ row }: Readonly<{ row: any }>) => {
  const text = row.isCompleted ? 'Completed' : 'Not Completed';
  const dotStyle = row.isCompleted
    ? {
        width: '0.583rem',
        height: '0.583rem',
        backgroundColor: '#43ac50',
        marginRight: '10px',
        borderRadius: '50%',
      }
    : {
        width: '0.583rem',
        height: '7px',
        backgroundColor: '#fbab45',
        marginRight: '10px',
        borderRadius: '50%',
      };

  return (
    <section style={{ display: 'flex', flexDirection: 'row' }}>
      <div style={dotStyle} />
      <div style={{ fontSize: '1rem', lineHeight: 1.33, color: '#9aa0ae' }}>
        {text}
      </div>
    </section>
  );
};

const gcpColumnConfig: ColumnConfig = {
  findingSourcepropertiesExplanation: {
    name: 'findingSourcepropertiesExplanation',
    title: 'Findings',
    width: 300,
    getCellValue: (row: any) => {
      if (row.findingSourcepropertiesRecommendation) {
        return (
          <SimpleTooltip
            title={
              <Interweave content={row.findingSourcepropertiesRecommendation} />
            }
          >
            <Interweave content={row.findingSourcepropertiesExplanation} />
          </SimpleTooltip>
        );
      }
      return '';
    },
  },
  findingSourcepropertiesRecommendation: {
    name: 'findingSourcepropertiesRecommendation',
    title: 'Action',
    show: false,
    width: 200,
    getCellValue: (row: any) => {
      if (row.findingSourcepropertiesRecommendation) {
        return (
          <Interweave content={row.findingSourcepropertiesRecommendation} />
        );
      }
      return '';
    },
  },
  resourcepropertiesName: {
    name: 'resourcepropertiesName',
    title: 'Resource Type',
    width: 150,
    getCellValue: (row: any) => {
      if (row.resourcepropertiesName) {
        return (
          <SimpleTooltip
            title={
              row.resourcepropertiesName.length > 50
                ? row.resourcepropertiesName
                : ''
            }
          >
            <>{truncate(row.resourcepropertiesName)}</>
          </SimpleTooltip>
        );
      }
      return '';
    },
  },
  findingResourcename: {
    name: 'findingResourcename',
    title: 'Resource Name',
    show: false,
    width: 150,
    getCellValue: (row: any) => {
      if (row.findingResourcename) {
        return (
          <SimpleTooltip
            title={
              row.findingResourcename.length > 50 ? row.findingResourcename : ''
            }
          >
            <>{truncate(row.findingResourcename)}</>
          </SimpleTooltip>
        );
      }
      return '';
    },
  },
  findingCategory: {
    name: 'findingCategory',
    title: 'Finding Category',
    show: false,
    width: 150,
    getCellValue: (row: any) => row.findingCategory,
  },
  type: {
    name: 'type',
    title: 'Category',
    width: 50,
  },
  findingSourcepropertiesSeveritylevel: {
    name: 'findingSourcepropertiesSeveritylevel',
    title: 'User Impact',
    width: 50,
  },
};

const microsoftColumnConfig: ColumnConfig = {
  title: {
    name: 'title',
    title: 'Improvement action',
    width: 500,
  },
  score: {
    name: 'score',
    title: 'Score',
    width: 50,
  },
  maxScore: {
    name: 'maxScore',
    title: 'MaxScore',
    width: 50,
  },
  controlCategory: {
    name: 'controlCategory',
    title: 'Category',
    width: 100,
  },
  userImpact: {
    name: 'userImpact',
    title: 'User Impact',
    width: 100,
  },
  implementationCost: {
    name: 'implementationCost',
    title: 'Implementation Cost',
    width: 150,
  },
  isCompleted: {
    name: 'isCompleted',
    title: 'Status',
    width: 250,
    getCellValue: (row: any) => <IsCompletedCell row={row} />,
  },
};

const securityStudioColumnConfig: ColumnConfig = {
  recommendationTaskName: {
    name: 'recommendationTaskName',
    title: 'Controls',
    width: 400,
  },
  name: {
    name: 'name',
    title: 'Category',
    width: 200,
  },
  type: {
    name: 'type',
    title: 'Task',
    width: 100,
  },
};

const columnConfigByType: Record<string, ColumnConfig> = {
  google: gcpColumnConfig,
  microsoft: microsoftColumnConfig,
  securitystudio: securityStudioColumnConfig,
};

const useColumnConfig = (connectorType: string): ColumnConfig =>
  useMemo(() => columnConfigByType[connectorType] ?? {}, [connectorType]);

const scoreTypes = [
  { label: 'Total Score', value: 'avgOverallScore' },
  { label: 'Identity', value: 'avgIdentityScore' },
  { label: 'Data', value: 'avgDataScore' },
  { label: 'Device', value: 'avgDeviceScore' },
  { label: 'Apps', value: 'avgAppsScore' },
];

const ConnectorDetailsBase = ({ classes, connectorId }: any) => {
  const location = useLocation();
  const { query } = useQueryParams();
  const handleAPIError = useAPIErrorHandler();

  const pathname = location.pathname.replace(
    '/customer/connector/connector-details/',
    ''
  );
  const connectorType = pathname.split('/')[0];

  const [myData, setMyData] = useState(null);
  const myScoreType = { label: 'Total Score', value: 'avgOverallScore' };
  const [myConnectorId, setMyConnectorId] = useState(connectorId);
  const [gcpSelectedLines, setGcpSelectedLines] = useState({
    series1: true,
    series2: true,
    series3: true,
    series4: true,
    series5: true,
    series6: true,
  });

  const [msSelectedLines, setMsSelectedLines] = useState({
    series1: true,
    series2: true,
    series3: true,
  });

  const [securityStudioSelectedLines, setSecurityStudioSelectedLines] =
    useState({
      series1: true,
      series2: true,
      series3: true,
    });

  const onToggleLineHandler = (name: any, type: any) => () => {
    const mappings1 = {
      google: gcpSelectedLines,
      securitystudio: securityStudioSelectedLines,
      microsoft: msSelectedLines,
    };

    const mappings2 = {
      google: setGcpSelectedLines,
      securitystudio: setSecurityStudioSelectedLines,
      microsoft: setMsSelectedLines,
    };

    const getVar = mappings1[type];
    const setVar = mappings2[type];

    const val = !getVar[name];
    getVar[name] = val;
    PubSub.publish('ConnectorDetails:toggleLines', getVar);
    setVar((prevState: any) => {
      return {
        ...prevState,
        [name]: val,
      };
    });
  };

  const selectScoreTypesHandler = (name: any, values: any) => {
    PubSub.publish('connectorDetails:scoreType-scoreHistory', values.value);
    setMsSelectedLines({
      series1: true,
      series2: true,
      series3: true,
    });
  };

  const renderGCPScoreHistory = () => {
    const { after, before } = query;
    const days = moment.unix(before).diff(moment.unix(after), 'days');
    return (
      <section className={classes.scoreHistoryContainer}>
        <h3 className={classes.scoresHistoryTitle}>
          GOOGLE COMMAND CENTER - HISTORY
        </h3>
        <section className={classes.scoresHistoryContainer}>
          <section className={classes.chartLegends}>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series1', 'google')}
            >
              <div
                className={`${
                  gcpSelectedLines.series1
                    ? classes.legendIcon4
                    : classes.emptyLegendIcon4
                }`}
              />
              <p className={classes.legendText4}>Total Score</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series2', 'google')}
            >
              <div
                className={`${
                  gcpSelectedLines.series2
                    ? classes.legendIcon5
                    : classes.emptyLegendIcon5
                }`}
              />
              <p className={classes.legendText5}>Identity</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series3', 'google')}
            >
              <div
                className={`${
                  gcpSelectedLines.series3
                    ? classes.legendIcon6
                    : classes.emptyLegendIcon6
                }`}
              />
              <p className={classes.legendText6}>Network</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series4', 'google')}
            >
              <div
                className={`${
                  gcpSelectedLines.series4
                    ? classes.legendIcon7
                    : classes.emptyLegendIcon7
                }`}
              />
              <p className={classes.legendText7}>Data</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series5', 'google')}
            >
              <div
                className={`${
                  gcpSelectedLines.series5
                    ? classes.legendIcon8
                    : classes.emptyLegendIcon8
                }`}
              />
              <p className={classes.legendText8}>Infrastructure</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series6', 'google')}
            >
              <div
                className={`${
                  gcpSelectedLines.series6
                    ? classes.legendIcon9
                    : classes.emptyLegendIcon9
                }`}
              />
              <p className={classes.legendText9}>Encryption</p>
            </Box>
          </section>
          <section className={classes.chartsContainer}>
            <GCPScoreHistory
              connectorId={myConnectorId}
              connectorType={connectorType}
              days={days}
              scoreType={myScoreType.value}
              q={query}
            />
          </section>
        </section>
      </section>
    );
  };

  const renderSecurityStudioScoreHistory = () => {
    const { after, before } = query;
    const days = moment.unix(before).diff(moment.unix(after), 'days');
    return (
      <section className={classes.scoreHistoryContainer}>
        <h3 className={classes.scoresHistoryTitle}>
          SECURITY STUDIO - HISTORY
        </h3>
        <section className={classes.scoresHistoryContainer}>
          <section className={classes.chartLegends}>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series1', 'securitystudio')}
            >
              <div
                className={`${
                  gcpSelectedLines.series1
                    ? classes.legendIcon4
                    : classes.emptyLegendIcon4
                }`}
              />
              <p className={classes.legendText4}>Total Score</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series2', 'securitystudio')}
            >
              <div
                className={`${
                  gcpSelectedLines.series2
                    ? classes.legendIcon5
                    : classes.emptyLegendIcon5
                }`}
              />
              <p className={classes.legendText5}>Administrative</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series3', 'securitystudio')}
            >
              <div
                className={`${
                  gcpSelectedLines.series3
                    ? classes.legendIcon6
                    : classes.emptyLegendIcon6
                }`}
              />
              <p className={classes.legendText6}>Physical</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series4', 'securitystudio')}
            >
              <div
                className={`${
                  gcpSelectedLines.series2
                    ? classes.legendIcon7
                    : classes.emptyLegendIcon7
                }`}
              />
              <p className={classes.legendText7}>Internal</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series5', 'securitystudio')}
            >
              <div
                className={`${
                  gcpSelectedLines.series2
                    ? classes.legendIcon8
                    : classes.emptyLegendIcon8
                }`}
              />
              <p className={classes.legendText8}>External</p>
            </Box>
          </section>
          <section className={classes.chartsContainer}>
            <SecurityStudioScoreHistory
              connectorId={myConnectorId}
              connectorType={connectorType}
              days={days}
              scoreType={myScoreType.value}
              q={query}
            />
          </section>
        </section>
      </section>
    );
  };

  const renderMicrosoftScoreHistory = () => {
    const { after, before } = query;
    const days = moment.unix(before).diff(moment.unix(after), 'days');
    return (
      <section className={classes.scoreHistoryContainer}>
        <h3 className={classes.scoresHistoryTitle}>
          Microsoft secure score - HISTORY
        </h3>
        <section className={classes.scoresHistoryContainer}>
          <section className={classes.chartLegends}>
            <section className={classes.dropdown}>
              <MultiSelect
                name="scoreType"
                isMulti={false}
                options={scoreTypes}
                onChange={selectScoreTypesHandler}
                values={myScoreType}
                required
                classes={{
                  select: classes.button,
                }}
              />
            </section>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series1', 'microsoft')}
            >
              <div
                className={`${
                  msSelectedLines.series1
                    ? classes.legendIcon4
                    : classes.emptyLegendIcon4
                }`}
              />
              <p className={classes.legendText4}>Your Score</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series2', 'microsoft')}
            >
              <div
                className={`${
                  msSelectedLines.series2
                    ? classes.legendIcon5
                    : classes.emptyLegendIcon5
                }`}
              />
              <p className={classes.legendText5}>Similar seat Count</p>
            </Box>
            <Box
              className={classes.legendItem}
              onClick={onToggleLineHandler('series3', 'microsoft')}
            >
              <div
                className={`${
                  msSelectedLines.series3
                    ? classes.legendIcon6
                    : classes.emptyLegendIcon6
                }`}
              />
              <p className={classes.legendText6}>Global average</p>
            </Box>
          </section>
          <section className={classes.chartsContainer}>
            <MSScoreHistory
              connectorId={myConnectorId}
              connectorType={connectorType}
              days={days}
              scoreType={myScoreType.value}
              q={query}
            />
          </section>
        </section>
      </section>
    );
  };

  useEffect(() => {
    if (!myData) {
      Promise.all([
        getConnectorScore({ connectorType, connectorId: myConnectorId }),
      ])
        .then((responses) => {
          setMyData({
            scores: responses[0].data,
          });
        })
        .catch(console.error.bind(console));
    }

    const sub = PubSub.subscribe(
      'ConnectorDetailsContainer:render',
      (id: any) => {
        setMyConnectorId(id);
        setMyData(null);
      }
    );

    return () => {
      sub.remove();
    };
  }, [connectorType, myConnectorId, myData]);

  const columnConfig = useColumnConfig(connectorType);

  const { uiSettings } = useSavedSettings(TABLE_ID, columnConfig);
  useBackfill(BACKFILL, uiSettings);

  const { queryHandlers } = useQueryHandlers(query);
  const { state, columns, handlers } = useTableState(columnConfig, {
    uiSettings,
  });

  useUiSettingsAutosave(
    TABLE_ID,
    {
      columns,
      columnOrder: state.columnOrder,
      ...query,
    },
    ['before', 'after']
  );

  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: [TABLE_ID, connectorType, myConnectorId, query],
    queryFn: () =>
      getActionList({ connectorType, connectorId: myConnectorId, ...query }),
    select: (response: any) => ({
      rows: response.data.content,
      totalCount: response.data.totalElements,
    }),
    onError: handleAPIError('Unable to retrieve connector details.'),
  });

  const onRefetch = () => {
    queryClient.invalidateQueries([TABLE_ID]);
  };

  const mergedState = useMemo(() => ({ ...state, ...query }), [query, state]);
  const mergedHandlers = useMemo(
    () => ({ ...handlers, ...queryHandlers }),
    [handlers, queryHandlers]
  );

  function renderMicrosoftScoresSection() {
    if (myData && myData.scores) {
      const { scores } = myData;
      const { averageComparativeScores = [] } = scores;
      let result = null;
      averageComparativeScores.forEach((item) => {
        if (item.basis === 'me') {
          const {
            averageScore,
            maxScore,
            deviceScore,
            dataScore,
            identityScore,
            appsScore,
            maxDeviceScore,
            maxDataScore,
            maxIdentityScore,
            maxAppsScore,
            infrastructureScore,
            maxInfrastructureScore,
          } = item;
          result = (
            <section className={classes.scoresSectionContainer}>
              <section className={classes.sectionTitleContainer}>
                <h3 className={classes.title}>MICROSOFT SECURE SCORE</h3>
                <section className={classes.asOfContainer}>
                  <span className={classes.asOf}>
                    As of:
                    {toUniversalUtcDate(scores.lastPull)}
                  </span>
                  <SimpleTooltip
                    className={classes.simpleTooltip}
                    title="Actions you can take to improve your Microsoft Secure Score, may take up to 24 hours to update the Score."
                  >
                    <img
                      src="/images/info.svg"
                      className={classes.tooltipIcon}
                      alt="ToolTip"
                    />
                  </SimpleTooltip>
                </section>
              </section>
              <section className={classes.scoresContainer}>
                <section className={classes.totalScoresContainer}>
                  <p className={classes.totalScoreTitle}>Total Score</p>
                  <div className={classes.totalScoresCol}>
                    <p className={classes.myScore}>
                      {averageScore}
                      <span className={classes.maxScore}> / {maxScore}</span>
                    </p>
                  </div>
                </section>
                <section className={classes.verticalLine} />
                <section className={classes.otherScoresContainer}>
                  <section className={classes.scoreLine}>
                    <div className={classes.scoreHeaderContainer}>
                      <p className={classes.scoreHeader}>Identity</p>
                    </div>
                    <div className={classes.scoresColum}>
                      {typeof identityScore === 'undefined' ? (
                        <p className={classes.noDataShow}>No data to show</p>
                      ) : (
                        <p className={classes.myScore}>
                          {identityScore}
                          <span className={classes.maxScore}>
                            {' '}
                            / {maxIdentityScore}
                          </span>
                        </p>
                      )}
                    </div>
                  </section>
                  <section className={classes.scoreLine}>
                    <div className={classes.scoreHeaderContainer}>
                      <p className={classes.scoreHeader}>Data</p>
                    </div>
                    <div className={classes.scoresColum}>
                      {typeof dataScore === 'undefined' ? (
                        <p className={classes.noDataShow}>No data to show</p>
                      ) : (
                        <p className={classes.myScore}>
                          {dataScore}
                          <span className={classes.maxScore}>
                            {' '}
                            / {maxDataScore}
                          </span>
                        </p>
                      )}
                    </div>
                  </section>
                  <section className={classes.scoreLine}>
                    <div className={classes.scoreHeaderContainer}>
                      <p className={classes.scoreHeader}>Device</p>
                    </div>
                    <div className={classes.scoresColum}>
                      {typeof deviceScore === 'undefined' ? (
                        <p className={classes.noDataShow}>No data to show</p>
                      ) : (
                        <p className={classes.myScore}>
                          {deviceScore}
                          <span className={classes.maxScore}>
                            {' '}
                            / {maxDeviceScore}
                          </span>
                        </p>
                      )}
                    </div>
                  </section>
                  <section className={classes.scoreLine}>
                    <div className={classes.scoreHeaderContainer}>
                      <p className={classes.scoreHeader}>Apps</p>
                    </div>
                    <div className={classes.scoresColum}>
                      {typeof appsScore === 'undefined' ? (
                        <p className={classes.noDataShow}>No data to show</p>
                      ) : (
                        <p className={classes.myScore}>
                          {appsScore}
                          <span className={classes.maxScore}>
                            {' '}
                            / {maxAppsScore}
                          </span>
                        </p>
                      )}
                    </div>
                  </section>
                  <section className={classes.scoreLine}>
                    <div className={classes.scoreHeaderContainer}>
                      <p className={classes.scoreHeader}>Infrastructure</p>
                    </div>
                    <div className={classes.scoresColum}>
                      {typeof infrastructureScore === 'undefined' ? (
                        <p className={classes.noDataShow}>No data to show</p>
                      ) : (
                        <p className={classes.myScore}>
                          {infrastructureScore}
                          <span className={classes.maxScore}>
                            {' '}
                            / {maxInfrastructureScore}
                          </span>
                        </p>
                      )}
                    </div>
                  </section>
                </section>
              </section>
            </section>
          );
        }
      });
      return result;
    }
    return null;
  }

  function renderGCPScoresSection() {
    if (myData && myData.scores) {
      const { scores } = myData;
      const { total, encryption, identity, infrastructure, network, data } =
        scores;
      return (
        <section className={classes.scoresSectionContainer}>
          <section className={classes.sectionTitleContainer}>
            <h3 className={classes.title}>GOOGLE COMMAND CENTER SCORE</h3>
            <section className={classes.asOfContainer}>
              <span className={classes.asOf}>
                As of:
                {toUniversalUtcDate(scores.lastPull)}
              </span>
              <SimpleTooltip
                className={classes.simpleTooltip}
                title="Actions you can take to improve your Google Command Center Score, may take up to 24 hours to update the Score."
              >
                <img
                  src="/images/info.svg"
                  className={classes.tooltipIcon}
                  alt="ToolTip"
                />
              </SimpleTooltip>
            </section>
          </section>
          <section className={classes.scoresContainer}>
            <section className={classes.totalScoresContainer}>
              <p className={classes.totalScoreTitle}>Total Score</p>
              <div className={classes.totalScoresCol}>
                <p className={classes.myScore}>
                  {total.totalCount}
                  <span className={classes.maxScore}>
                    {' '}
                    ={total.highCount} +{' '}
                    <span className={classes.mediumCount}>
                      {total.mediumCount}
                    </span>
                  </span>
                </p>
              </div>
            </section>
            <section className={classes.verticalLine} />
            <section className={classes.otherScoresContainer}>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>Identity</p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof identity === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>
                      {identity.totalCount}
                      <span className={classes.maxScore}>
                        {' '}
                        ={identity.highCount} +{' '}
                        <span className={classes.mediumCount}>
                          {identity.mediumCount}
                        </span>
                      </span>
                    </p>
                  )}
                </div>
              </section>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>Network</p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof network === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>
                      {network.totalCount}
                      <span className={classes.maxScore}>
                        {' '}
                        ={network.highCount} +{' '}
                        <span className={classes.mediumCount}>
                          {network.mediumCount}
                        </span>
                      </span>
                    </p>
                  )}
                </div>
              </section>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>Data</p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof data === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>
                      {data.totalCount}
                      <span className={classes.maxScore}>
                        {' '}
                        ={data.highCount} +{' '}
                        <span className={classes.mediumCount}>
                          {data.mediumCount}
                        </span>
                      </span>
                    </p>
                  )}
                </div>
              </section>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>Infrastructure</p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof infrastructure === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>
                      {infrastructure.totalCount}
                      <span className={classes.maxScore}>
                        {' '}
                        = {infrastructure.highCount} +{' '}
                        <span className={classes.mediumCount}>
                          {infrastructure.mediumCount}
                        </span>
                      </span>
                    </p>
                  )}
                </div>
              </section>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>Encryption</p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof encryption === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>
                      {encryption.totalCount}
                      <span className={classes.maxScore}>
                        {' '}
                        ={encryption.highCount} +{' '}
                        <span className={classes.mediumCount}>
                          {encryption.mediumCount}
                        </span>
                      </span>
                    </p>
                  )}
                </div>
              </section>
            </section>
          </section>
        </section>
      );
    }
    return null;
  }

  function renderSecurityStudioScoresSection() {
    if (myData && myData.scores) {
      const { scores } = myData;
      const {
        total,
        administrativeControl,
        externalTechnicalControl,
        internalTechnicalControl,
        physicalControl,
      } = scores;
      return (
        <section className={classes.scoresSectionContainer}>
          <section className={classes.sectionTitleContainer}>
            <h3 className={classes.title}>SECURITY STUDIO SCORE</h3>
            <section className={classes.asOfContainer}>
              <span className={classes.asOf}>
                As of: {toUniversalUtcDate(scores.lastPull)}
              </span>
              <SimpleTooltip
                className={classes.simpleTooltip}
                title="Actions you can take to improve your Security Studio Score, may take up to 24 hours to update the Score."
              >
                <img
                  src="/images/info.svg"
                  className={classes.tooltipIcon}
                  alt="ToolTip"
                />
              </SimpleTooltip>
            </section>
          </section>
          <section className={classes.scoresContainer}>
            <section className={classes.totalScoresContainer}>
              <p className={classes.totalScoreTitle}>Total Score</p>
              <div className={classes.totalScoresCol}>
                <p className={classes.myScore}>{total}</p>
              </div>
            </section>
            <section className={classes.verticalLine} />
            <section className={classes.otherScoresContainer}>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>Administrative Control</p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof administrativeControl === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>{administrativeControl}</p>
                  )}
                </div>
              </section>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>
                    External Technical Control
                  </p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof externalTechnicalControl === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>
                      {externalTechnicalControl}
                    </p>
                  )}
                </div>
              </section>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>
                    Internal Technical Control
                  </p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof internalTechnicalControl === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>
                      {internalTechnicalControl}
                    </p>
                  )}
                </div>
              </section>
              <section className={classes.scoreLine}>
                <div className={classes.scoreHeaderContainer}>
                  <p className={classes.scoreHeader}>Physical Control</p>
                </div>
                <div className={classes.scoresColum}>
                  {typeof physicalControl === 'undefined' ? (
                    <p className={classes.noDataShow}>No data to show</p>
                  ) : (
                    <p className={classes.myScore}>{physicalControl}</p>
                  )}
                </div>
              </section>
            </section>
          </section>
        </section>
      );
    }
    return null;
  }

  const renderScoresSectionByType: Record<string, () => React.ReactNode> = {
    microsoft: renderMicrosoftScoresSection,
    google: renderGCPScoresSection,
    securitystudio: renderSecurityStudioScoresSection,
  };

  const renderScoreHistoryByType: Record<string, () => React.ReactNode> = {
    microsoft: renderMicrosoftScoreHistory,
    google: renderGCPScoreHistory,
    securitystudio: renderSecurityStudioScoreHistory,
  };

  const tableHeaderName =
    connectorType === 'securitystudio'
      ? 'Controls'
      : 'Findings And Improvement Actions';

  if (!myData) {
    return null;
  }

  return (
    <section>
      {renderScoreHistoryByType[connectorType]?.()}
      {renderScoresSectionByType[connectorType]?.()}

      <h1 className={`table-heading ${classes.headingMargins}`}>
        {tableHeaderName}
      </h1>

      <PrimaryToolbar>
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Box width="60%">
            <TableSearch placeholder="Search" />
          </Box>
        </Box>
      </PrimaryToolbar>

      <TableStateProvider
        state={mergedState}
        handlers={mergedHandlers}
        columns={columns}
        rows={data?.rows ?? []}
        tableId={TABLE_ID}
        loading={isFetching}
      >
        <SecondaryToolbar justifyContent="flex-end">
          <ColumnChooser />
          <CsvDownloadButton csvDownloadTitle={tableHeaderName} />
          <RefreshButton onClick={onRefetch} loading={isFetching} />
        </SecondaryToolbar>

        <BaseTable>
          <RemotePagingPlugin totalCount={data?.totalCount ?? 0} />
          <ColumnResizingPlugin />
        </BaseTable>
      </TableStateProvider>
    </section>
  );
};

export const ConnectorDetails = withStyles(styles as any)(ConnectorDetailsBase);
